import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import crmService, { DuplicateCluster, DedupeLead } from '../services/crmService';
import { PageHeader, useConfirm } from '../components/ui';
import { routeById } from '../navigation/routes';
import { useBerechtigung } from '../berechtigungen/useBerechtigung';
import { RechteHinweis } from '../vertrieb/RechteHinweis';

const scoreColor = (n: number): string => (n >= 0.9 ? 'var(--sec-danger)' : n >= 0.85 ? 'var(--sec-warning)' : 'var(--sec-info)');

const leadLine = (l: DedupeLead): string => {
  const name = [l.firstName, l.lastName].filter(Boolean).join(' ') || '(ohne Namen)';
  return [name, l.jobTitle, l.email].filter(Boolean).join(' · ');
};

const DuplicatesPage: React.FC = () => {
  const { confirm } = useConfirm();
  // Scannen ist ein GET (vertrieb.lesen); Zusammenfuehren loescht Leads (vertrieb.loeschen).
  const { darf } = useBerechtigung();
  const darfZusammenfuehren = darf('vertrieb.loeschen');
  const [clusters, setClusters] = useState<DuplicateCluster[]>([]);
  const [loading, setLoading] = useState(false);
  const [scanned, setScanned] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  // chosen primary per cluster (keyed by cluster index)
  const [primarySel, setPrimarySel] = useState<Record<number, string>>({});
  const [mergingIdx, setMergingIdx] = useState<number | null>(null);

  const scan = async () => {
    try {
      setLoading(true);
      setError('');
      setInfo('');
      const { clusters: c } = await crmService.getDuplicates();
      setClusters(c);
      setScanned(true);
      setPrimarySel(Object.fromEntries(c.map((cl, i) => [i, cl.suggestedPrimaryId])));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Scannen');
    } finally {
      setLoading(false);
    }
  };

  const merge = async (cluster: DuplicateCluster, idx: number) => {
    const primaryId = primarySel[idx] || cluster.suggestedPrimaryId;
    const duplicateIds = cluster.leadIds.filter((id) => id !== primaryId);
    if (duplicateIds.length === 0) return;
    const ok = await confirm({
      title: `${duplicateIds.length} Duplikat(e) zusammenführen?`,
      message: `Die Angaben wandern in den Primär-Lead, die ${duplicateIds.length} anderen Leads werden gelöscht. Rückgängig nur durch Neuanlage.`,
      confirmLabel: 'Zusammenführen',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      setMergingIdx(idx);
      setError('');
      await crmService.merge(primaryId, duplicateIds);
      setClusters((prev) => prev.filter((_, i) => i !== idx));
      setInfo('Cluster zusammengeführt.');
      setTimeout(() => setInfo(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Zusammenführen');
    } finally {
      setMergingIdx(null);
    }
  };

  return (
    <div className="page-container">
      <PageHeader
        title="Duplikate"
        subtitle={routeById('duplicates')?.description}
        actions={(
          <button className="btn btn-primary" onClick={scan} disabled={loading}>
            {loading ? 'Scanne…' : 'Duplikate scannen'}
          </button>
        )}
      />
      <p style={{ color: 'var(--sec-fg-muted)', marginBottom: '24px' }}>
        Findet doppelte Leads über mehrere Signale (LinkedIn, Name + Firma, Name + Domain, Telefon)
        und schlägt eine Zusammenführung vor — powered by Skill <em>crm-duplicate-detector</em>.
      </p>

      <RechteHinweis recht="vertrieb.loeschen" was="Zusammenführen" />

      {error && <div className="alert alert-error">{error}</div>}
      {info && <div className="alert alert-success">{info}</div>}

      {scanned && clusters.length === 0 && (
        <div className="empty-state">
          <div className="empty-state-description">Keine Duplikate gefunden. Saubere Liste! 🎉</div>
        </div>
      )}

      {clusters.map((cluster, idx) => {
        const primaryId = primarySel[idx] || cluster.suggestedPrimaryId;
        return (
          <div className="card" key={cluster.leadIds.join('-')} style={{ borderLeft: `4px solid ${scoreColor(cluster.score)}`, marginBottom: '16px' }}>
            <div className="card-header">
              <div className="card-title">
                {cluster.leadIds.length} mögliche Duplikate · Score {Math.round(cluster.score * 100)}%
              </div>
              {darfZusammenfuehren && (
                <button
                  className="btn btn-success btn-sm"
                  onClick={() => merge(cluster, idx)}
                  disabled={mergingIdx === idx}
                >
                  {mergingIdx === idx ? 'Führe zusammen…' : 'Zusammenführen'}
                </button>
              )}
            </div>

            <div style={{ fontSize: '12px', color: 'var(--sec-fg-subtle)', margin: '4px 0 12px' }}>
              {cluster.reasons.join(' · ')}
            </div>

            {cluster.leads.map((l) => (
              <label
                key={l.id}
                style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: 'var(--sec-fg)', marginBottom: '6px', cursor: 'pointer' }}
              >
                <input
                  type="radio"
                  name={`primary-${idx}`}
                  checked={primaryId === l.id}
                  disabled={!darfZusammenfuehren}
                  onChange={() => setPrimarySel({ ...primarySel, [idx]: l.id })}
                />
                <span>
                  {leadLine(l)}
                  {l.company && (
                    <>
                      {' · '}
                      <Link to={`/companies?suche=${encodeURIComponent(l.company)}`} title="Firma im CRM suchen" onClick={(e) => e.stopPropagation()}>
                        {l.company}
                      </Link>
                    </>
                  )}
                  {primaryId === l.id && (
                    <strong style={{ color: 'var(--sec-success)', marginLeft: '6px' }}>← Primär (behalten)</strong>
                  )}
                </span>
              </label>
            ))}

            <div style={{ marginTop: '10px', fontSize: '12px', color: 'var(--sec-fg-muted)' }}>
              <strong>Ergebnis nach Merge:</strong>{' '}
              {Object.entries(cluster.mergedPreview)
                .filter(([, v]) => v)
                .map(([k, v]) => `${k}: ${v}`)
                .join(' · ')}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default DuplicatesPage;
